import { useState } from 'react';
import { X, Play, CheckCircle, XCircle, ArrowRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTestResolution } from '@/hooks/useWorkflows';
import type { WorkflowType, WorkflowEnvironment, ResolutionTestResponse } from '@/lib/types';

interface Props {
  types: WorkflowType[];
  onClose: () => void;
}

export default function ResolutionTester({ types, onClose }: Props) {
  const [typeName, setTypeName] = useState('');
  const [subtype, setSubtype] = useState('');
  const [version, setVersion] = useState('');
  const [environment, setEnvironment] = useState<WorkflowEnvironment>('production');
  const [scope, setScope] = useState<'global' | 'customer'>('global');

  const testResolution = useTestResolution();
  const [result, setResult] = useState<ResolutionTestResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleTest = async () => {
    if (!typeName) return;

    setError(null);
    try {
      const response = await testResolution.mutateAsync({
        workflow_type: typeName,
        workflow_subtype: subtype || undefined,
        workflow_version: version || undefined,
        environment,
        scope,
      });
      setResult(response);
    } catch (err) {
      setResult(null);
      setError(err instanceof Error ? err.message : 'Resolution test failed');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-background border border-border rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <h2 className="text-xl font-semibold">Resolution Tester</h2>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded-md hover:bg-muted transition-colors"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Input Form */}
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Test how a workflow template would be resolved for a given type, subtype, version, and environment.
              Leave subtype and version empty to test default resolution.
            </p>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label htmlFor="res-type" className="text-sm font-medium">Workflow Type</label>
                <select
                  id="res-type"
                  value={typeName}
                  onChange={(e) => setTypeName(e.target.value)}
                  className="w-full px-3 py-2 rounded-md bg-background border border-border focus:outline-none focus:ring-2 focus:ring-primary/50"
                >
                  <option value="">Select a type...</option>
                  {types.map((type) => (
                    <option key={type.name} value={type.name}>
                      {type.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <label htmlFor="res-env" className="text-sm font-medium">Environment</label>
                <select
                  id="res-env"
                  value={environment}
                  onChange={(e) => setEnvironment(e.target.value as WorkflowEnvironment)}
                  className="w-full px-3 py-2 rounded-md bg-background border border-border focus:outline-none focus:ring-2 focus:ring-primary/50"
                >
                  <option value="production">Production</option>
                  <option value="staging">Staging</option>
                  <option value="development">Development</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label htmlFor="res-subtype" className="text-sm font-medium">Subtype (optional)</label>
                <input
                  id="res-subtype"
                  type="text"
                  value={subtype}
                  onChange={(e) => setSubtype(e.target.value)}
                  placeholder="Leave empty to use default"
                  className="w-full px-3 py-2 rounded-md bg-background border border-border focus:outline-none focus:ring-2 focus:ring-primary/50"
                />
              </div>
              <div className="space-y-2">
                <label htmlFor="res-version" className="text-sm font-medium">Version (optional)</label>
                <input
                  id="res-version"
                  type="text"
                  value={version}
                  onChange={(e) => setVersion(e.target.value)}
                  placeholder="Leave empty to use default"
                  className="w-full px-3 py-2 rounded-md bg-background border border-border focus:outline-none focus:ring-2 focus:ring-primary/50"
                />
              </div>
            </div>

            <fieldset className="space-y-2">
              <legend className="text-sm font-medium">Scope</legend>
              <div className="flex gap-4">
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="scope"
                    value="global"
                    checked={scope === 'global'}
                    onChange={() => setScope('global')}
                  />
                  <span className="text-sm">Global (no customer context)</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="scope"
                    value="customer"
                    checked={scope === 'customer'}
                    onChange={() => setScope('customer')}
                  />
                  <span className="text-sm">Customer (uses your API key's customer)</span>
                </label>
              </div>
            </fieldset>

            <button
              type="button"
              onClick={handleTest}
              disabled={!typeName || testResolution.isPending}
              className="flex items-center gap-2 px-4 py-2 rounded-md bg-primary hover:bg-primary/90 text-primary-foreground transition-colors disabled:opacity-50"
            >
              <Play className="h-4 w-4" />
              {testResolution.isPending ? 'Testing...' : 'Test Resolution'}
            </button>
          </div>

          {/* Error */}
          {error && (
            <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400">
              <div className="flex items-center gap-2">
                <XCircle className="h-5 w-5 flex-shrink-0" />
                <span>{error}</span>
              </div>
            </div>
          )}

          {/* Results */}
          {result && (
            <div className="space-y-4">
              <h3 className="text-lg font-medium">Resolution Result</h3>

              {/* Default Resolution */}
              <div className="p-4 rounded-lg bg-muted/30 border border-border space-y-3">
                <h4 className="text-sm font-medium flex items-center gap-2">
                  <span>Default Resolution</span>
                  {result.resolved_default ? (
                    <CheckCircle className="h-4 w-4 text-green-400" />
                  ) : (
                    <XCircle className="h-4 w-4 text-red-400" />
                  )}
                </h4>
                {result.resolved_default ? (
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <span className="text-muted-foreground">Subtype:</span>{' '}
                      <span className="font-mono">{result.resolved_default.default_subtype}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Version:</span>{' '}
                      <span className="font-mono">{result.resolved_default.default_version}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Source:</span>{' '}
                      <span
                        className={cn(
                          'px-2 py-0.5 rounded text-xs',
                          result.resolved_default.source === 'customer'
                            ? 'bg-purple-500/20 text-purple-400'
                            : 'bg-blue-500/20 text-blue-400'
                        )}
                      >
                        {result.resolved_default.source}
                      </span>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    No default configured for this type/environment
                  </p>
                )}
              </div>

              {/* Template Resolution */}
              <div className="p-4 rounded-lg bg-muted/30 border border-border space-y-3">
                <h4 className="text-sm font-medium flex items-center gap-2">
                  <span>Template Resolution</span>
                  {result.resolved_template ? (
                    <CheckCircle className="h-4 w-4 text-green-400" />
                  ) : (
                    <XCircle className="h-4 w-4 text-red-400" />
                  )}
                </h4>
                {result.resolved_template ? (
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <span className="text-muted-foreground">ID:</span>{' '}
                      <span className="font-mono text-xs">{result.resolved_template.id}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Type:</span>{' '}
                      <span className="font-mono">{result.resolved_template.type_name}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Subtype:</span>{' '}
                      <span className="font-mono">{result.resolved_template.subtype}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Version:</span>{' '}
                      <span className="font-mono">{result.resolved_template.version}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Environment:</span>{' '}
                      <span className="font-mono">{result.resolved_template.environment}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Source:</span>{' '}
                      <span
                        className={cn(
                          'px-2 py-0.5 rounded text-xs',
                          result.resolved_template.source === 'customer'
                            ? 'bg-purple-500/20 text-purple-400'
                            : 'bg-blue-500/20 text-blue-400'
                        )}
                      >
                        {result.resolved_template.source}
                      </span>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">No matching template found</p>
                )}
              </div>

              {/* Resolution Trace */}
              <div className="p-4 rounded-lg bg-muted/30 border border-border space-y-3">
                <h4 className="text-sm font-medium">Resolution Trace</h4>
                <div className="space-y-1">
                  {/* Static, never-reordered trace with no per-line id — the array index is the
                      collision-proof key here (react-doctor/no-array-index-as-key is overridden for
                      this file in doctor.config.ts). */}
                  {result.resolution_trace.map((step, idx) => (
                    <div key={idx} className="flex items-start gap-2 text-sm">
                      <ArrowRight className="h-4 w-4 mt-0.5 text-muted-foreground flex-shrink-0" />
                      <span className="text-muted-foreground">{step}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end px-6 py-4 border-t border-border">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-md bg-secondary hover:bg-secondary/80 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
